import React, { useEffect, useState } from "react";
import "./NavbarNew.css";
import logo from "../../assets/img/header-logo.png";
import * as auth from "../../backend/auth";
import { useAssociateCompanyLogo } from "../../contexts/AssociateCompanyContext";
import { preventDefault } from "../../utils/common";
import ChangePasswordModal from "../pages/Authenticate/ChangePasswordModal";
import { navigate } from "raviger";
import { getBasePath } from "../../App";
import Icon from "../common/Icon";
import NotificationsModal from "./NotificationsModal";
import ImpersonationBanner from "./ImpersonationBanner";

function NavbarNew({ showUser = true }) {
  const [user] = useState(auth.getUserDetails() || {});
  const [role, setRole] = useState("");
  const [roles, setRoles] = useState<any[]>([]);
  const [changePwd, setChangePwd] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState<any>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications] = useState([]);
  const { logo: associateCompanyLogo } = useAssociateCompanyLogo();

  // Calls POST /api/v1/auth/logout (via useLogout) so the backend actually
  // knows the session ended - previously this just called
  // auth.clearUserSession() directly with no API call at all, so an
  // impersonated session's ImpersonationSession record never got closed
  // out: it sat "Active" until the idle-timeout path eventually caught it
  // and mislabeled it idle_timeout, even though the user had explicitly
  // logged out. On error, still falls back to clearing client-side (same
  // as AuthProtector's auto-logout in LayoutWithSideNav.tsx) so the user is
  // never stuck unable to log out just because the API call failed.
  const { logout: performLogout } = auth.useLogout(
    () => {
      setTimeout(() => {
        // /login explicitly, not '/' — so the URL always matches what's on screen after logout.
        navigate(`${getBasePath()}/login`);
        window.location.reload();
      });
    },
    (error: any) => {
      console.error('Logout failed:', error);
      auth.clearUserSession();
      setTimeout(() => {
        navigate(`${getBasePath()}/login`);
        window.location.reload();
      });
    }
  );

  function logout(event: any) {
    preventDefault(event);
    performLogout();
  }

  function changeRole(_role: any) {
    const userDetails = auth.getUserDetails();
    if (!userDetails || !userDetails.roles) {
      return;
    }
    const index = userDetails.roles.findIndex((r: any) => r.id === _role.id);
    auth.setUserRole([_role]);
    setTimeout(() => {
      navigate(`${getBasePath()}/`);
      window.location.reload();
    });
  }

  useEffect(() => {
    if (notifications) {
      setUnreadNotifications(
        notifications.filter((x: any) => !x.isRead).length
      );
    }
  }, [notifications]);

  useEffect(() => {
    if (showUser) {
      const _role = auth.getUserRole();
      setRole(_role);
      const { roles } = auth.getUserDetails();
      if (roles.length === 1) {
        setRoles([]);
      } else {
        // Hide roles with no pages configured (e.g. Impersonation Admin) from "Login As" —
        // there's nothing on the UI to land on if you switch to one.
        setRoles(roles.filter((x: any) => x.name !== _role && (x.pages || []).length > 0));
      }
    }
  }, [showUser]);

  return (
    <>
      <div className="container-full bg-white header-navbar-container shadow">
        <nav className="navbar navbar-expand-lg border-bottom">
          <div className="container-fluid">
            <div className="navbar-brand p-0">
              {/* Nothing renders until the associate company has actually resolved
                  (associateCompanyLogo === undefined) — only fall back to the ezyCOMP
                  wordmark once we KNOW there's no associate company logo.
                  Sizing differs by design (per Pallavi Khamkar, design team):
                  the ezyCOMP default wordmark is sized by WIDTH — 150px, with
                  height auto-adjusting (comes out ~47px for this asset's own
                  aspect ratio) — while a custom company logo is capped by
                  HEIGHT at 48px (confirmed correct as-is) since uploaded logos
                  vary in aspect ratio and a fixed width would distort or
                  overflow them. The bar itself is only 68px tall with ~16px of
                  padding baked in (.header-navbar-container .navbar +
                  Bootstrap's own navbar padding), so both fit within it. */}
              {associateCompanyLogo !== undefined && (
                associateCompanyLogo
                  ? <img src={associateCompanyLogo} alt="Logo" style={{ maxHeight: 48, width: "auto" }} />
                  : <img src={logo} alt="Logo" style={{ width: 150, height: "auto" }} />
              )}
            </div>
            <ImpersonationBanner />
            {showUser && (
              <>
                <div className="nav-item d-flex align-items-center">
                  {(notifications || []).length > 0 && (
                    <div className="p-2 position-relative">
                      <Icon
                        name="notification"
                        style={{ cursor: "pointer" }}
                        action={() => setShowNotifications(true)}
                      />
                      {unreadNotifications && (
                        <span className="notification-badge">
                          {unreadNotifications > 9 ? "9+" : unreadNotifications}
                        </span>
                      )}
                    </div>
                  )}
                  <ul className="navbar-nav px-2">
                    <div className="nav-item dropdown">
                      <div
                        className="nav-link dropdown-toggle text-black"
                        role="button"
                        aria-expanded="false"
                      >
                        <span className="userNameL rounded-circle fw-semibold text-center text-white">
                          {(user.name || "").charAt(0)}
                        </span>{" "}
                        <div>
                          <div className="fw-semibold text-md">{user.name}</div>
                          <div className="text-sm text-gray-500">{role}</div>
                        </div>
                      </div>
                      <div className="dropdown-menu">
                        <div className="px-3 text-md fw-bold">
                          Role:
                        </div>
                        <div className="px-3 pb-3">{role}</div>
                        {roles.length > 0 && (
                          <>
                            <div className="px-3 text-md fw-bold">
                              Login As:
                            </div>
                            {roles.map((_role: any) => {
                              return (
                                <div
                                  className="dropdown-item"
                                  key={_role.name}
                                  onClick={() => changeRole(_role)}
                                >
                                  {_role.name}
                                </div>
                              );
                            })}
                          </>
                        )}
                        <hr className="dropdown-divider" />
                        <a className="dropdown-item" href="/" onClick={logout}>
                          Logout
                        </a>
                      </div>
                    </div>
                  </ul>
                </div>
              </>
            )}
          </div>
        </nav>
      </div>
      {changePwd && <ChangePasswordModal onClose={() => setChangePwd(false)} />}
      {showNotifications && (
        <NotificationsModal
          notifications={notifications}
          onDismiss={() => setShowNotifications(false)}
          onSubmit={() => {
            setShowNotifications(false);
            // refetch();
          }}
        />
      )}
    </>
  );
}

export default NavbarNew;
